import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { AlertTriangle, LayoutDashboard, RefreshCw } from 'lucide-react';
import Button from './Button';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkReload';

/**
 * Friendly, in-app replacement for React Router's developer error screen.
 *
 * Wired as ``errorElement`` on the top-level routes so ANY route crash — a lazy
 * chunk that failed to import, a render error inside a page, a bad loader — is
 * caught here and shown as a calm message with a Reload action, instead of the
 * raw "Unexpected Application Error" screen.
 */
export default function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const chunk = isChunkLoadError(error);

  // A failed route chunk almost always means the app was updated underneath this
  // tab. Reloading once picks up the new build; the helper throttles it so a
  // truly broken deploy shows this screen instead of looping.
  useEffect(() => {
    if (chunk) reloadOnceForChunkError();
  }, [chunk]);

  // Only surface a human-meaningful status; never leak raw JS error text to end users.
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : '';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-slate-950 px-6 text-center text-slate-100">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-400">
        <AlertTriangle size={28} aria-hidden="true" />
      </div>
      <div className="max-w-md space-y-2">
        <h1 className="text-xl font-semibold">
          {chunk ? 'Updating the app…' : 'Something went wrong'}
        </h1>
        <p className="text-sm text-slate-400">
          {chunk
            ? 'The app was updated while this page was open. If it does not refresh on its own, tap Reload.'
            : 'We hit an unexpected problem loading this page. Your data is safe — try again or head back to the dashboard.'}
        </p>
        {detail && !chunk && (
          <p className="break-words rounded-lg bg-slate-900 px-3 py-2 text-xs text-slate-500">{detail}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button variant="primary" onClick={() => window.location.reload()} data-agent-id="app.error.reload">
          <RefreshCw size={16} aria-hidden="true" /> Reload
        </Button>
        <Button variant="secondary" onClick={() => navigate('/app')} data-agent-id="app.error.dashboard">
          <LayoutDashboard size={16} aria-hidden="true" /> Go to dashboard
        </Button>
      </div>
    </div>
  );
}
